import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useChat } from '@/hooks/useChat';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface ChatPanelProps {
    playlistId: string;
}

export function ChatPanel({ playlistId }: ChatPanelProps) {
    const { user } = useAuth();
    const { messages, isLoading, sendMessage } = useChat(playlistId);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (e: FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        try {
            await sendMessage(newMessage.trim());
            setNewMessage('');
        } catch (error) {
            console.error('Failed to send message', error);
        }
    };

    const handleDelete = async (messageId: string) => {
        if (!confirm('Delete this message?')) return;
        try {
            // We need to import ChatService. 
            // But useChat doesn't expose delete.
            // Best to expose delete from useChat or import service here.
            const { ChatService } = await import('@/services/chat.service');
            await ChatService.deleteMessage(playlistId, messageId);
        } catch (error) {
            console.error('Failed to delete message', error);
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="d-flex flex-column h-100 bg-body-tertiary border-start">
            {/* Header */}
            <div className="p-3 border-bottom bg-white">
                <h5 className="mb-0">Live Chat</h5>
            </div>

            {/* Messages List */}
            <div className="flex-grow-1 overflow-auto p-3" style={{ height: '0px' /* hack for flex scroll */ }}>
                {messages.length === 0 ? (
                    <div className="text-center text-muted mt-5">
                        <small>No messages yet. Say hi!</small>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {messages.map((msg) => {
                            const isMe = user?.id === msg.userId;
                            const isSystem = msg.type === 'system';

                            if (isSystem) {
                                return (
                                    <div key={msg.id} className="text-center text-muted small my-1">
                                        <em>{msg.message}</em>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={msg.id}
                                    className={`d-flex ${isMe ? 'justify-content-end' : 'justify-content-start'}`}
                                >
                                    {!isMe && (
                                        <img
                                            src={msg.userAvatar || `https://ui-avatars.com/api/?name=${msg.userName}&background=random`}
                                            alt={msg.userName}
                                            className="rounded-circle me-2"
                                            width="32"
                                            height="32"
                                        />
                                    )}
                                    <div
                                        className={`p-2 rounded-3 ${isMe ? 'bg-primary text-white' : 'bg-body border'}`}
                                        style={{ maxWidth: '80%' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                                            <span className={`fw-bold small ${isMe ? 'text-white' : 'text-dark'}`}>
                                                {msg.userName || 'Unknown'}
                                            </span>
                                            <span className={`small ${isMe ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.7em' }}>
                                                {msg.createdAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className="small text-break">{msg.message}</div>
                                        {(user?.role === 'admin' || isMe) && (
                                            <div className="text-end mt-1">
                                                <button
                                                    className="btn btn-link btn-sm p-0 text-danger opacity-50 hover-opacity-100"
                                                    onClick={() => handleDelete(msg.id)}
                                                    style={{ fontSize: '0.7rem' }}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-3 border-top bg-body">
                <form onSubmit={handleSend} className="d-flex gap-2">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary" disabled={!newMessage.trim()}>
                        <i className="bi bi-send-fill"></i>
                    </button>
                </form>
            </div>
        </div>
    );
}
